import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { authService } from '../services/authService';
import { AuthUser } from '../types/auth';

interface AuthContextValue {
  user: AuthUser | null;
  /** True until the first session lookup settles. */
  loading: boolean;
  hasRole: (...roles: string[]) => boolean;
  refresh: () => Promise<void>;
  signOut: () => Promise<void>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

/**
 * Holds the session once for the whole tree. Previously every guard and every
 * chrome component called `getCurrentUser()` on mount, so a single page load
 * resolved the session four or five times and each one flashed its own spinner.
 */
export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [loading, setLoading] = useState(true);

  const refresh = useCallback(async () => {
    try {
      setUser(await authService.getCurrentUser());
    } catch {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const signOut = useCallback(async () => {
    try {
      await authService.logout();
    } finally {
      localStorage.clear();
      sessionStorage.clear();
      setUser(null);
      window.location.href = '/login';
    }
  }, []);

  const hasRole = useCallback(
    (...roles: string[]) => !!user && roles.some((role) => user.groups.includes(role)),
    [user],
  );

  const value = useMemo(
    () => ({ user, loading, hasRole, refresh, signOut }),
    [user, loading, hasRole, refresh, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used inside <AuthProvider>');
  return ctx;
};
